import { FieldValues } from 'react-hook-form';
import { DatePickerProps } from './props';
import { useIsMobile } from '@starsoft/common/hooks';
import DatePickerMobile from './Mobile';
import DatePickerDesktop from './Desktop';

/**
 * Component that renders a date picker with responsive mobile/desktop versions
 * Automatically switches between mobile and desktop versions based on screen size
 *
 * @component
 * @template T - Generic type for form values structure
 * @param {Object} props - Component props
 * @param {string} props.placeholder - Placeholder text to display when no date selected
 * @param {Function} props.onChange - Callback function when date selection changes
 * @param {string|Date} props.value - Currently selected date value
 * @param {boolean} props.disabled - Whether the date picker is disabled
 * @param {boolean} props.readOnly - Whether the date picker is read-only
 * @returns {JSX.Element} Date picker component appropriate for device type
 */
export function DatePicker<T extends FieldValues>({
  placeholder,
  onChange,
  value,
  disabled,
  readOnly,
  ...props
}: DatePickerProps<T>) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <DatePickerMobile
        {...props}
        onChange={onChange}
        value={value}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
      />
    );
  }

  return (
    <DatePickerDesktop
      {...props}
      onChange={onChange}
      value={value}
      disabled={disabled}
      readOnly={readOnly}
      placeholder={placeholder}
    />
  );
}
